import { TreeSelect } from 'antd';
import React, { forwardRef, useMemo } from 'react';

import { getOptionText, isDefinedValue } from './utils';

const streetTreeValuePrefix = 'street:';
const withoutStreetTreeValue = `${streetTreeValuePrefix}without-street`;

const getStreetTreeValue = (streetId) =>
  `${streetTreeValuePrefix}${String(streetId)}`;

const getHouseTreeData = ({ houseOptions = [], streetOptions = [] }) => {
  const houseValueMap = new Map();
  const streetNodeMap = new Map();
  const streetNodes = [];

  const createStreetNode = ({ streetId, title }) => {
    const streetValue = isDefinedValue(streetId)
      ? getStreetTreeValue(streetId)
      : withoutStreetTreeValue;
    const existingNode = streetNodeMap.get(streetValue);

    if (existingNode) {
      return existingNode;
    }

    const node = {
      key: streetValue,
      value: streetValue,
      title: title || 'Дома',
      search: title || 'Дома',
      selectable: false,
      children: [],
    };

    streetNodeMap.set(streetValue, node);
    streetNodes.push(node);
    return node;
  };

  streetOptions.forEach((streetOption) => {
    createStreetNode({
      streetId: streetOption.value,
      title: getOptionText(streetOption),
    });
  });

  houseOptions.forEach((houseOption) => {
    if (!isDefinedValue(houseOption.value)) {
      return;
    }

    const streetNode = createStreetNode({
      streetId: houseOption.streetId,
      title: houseOption.streetName,
    });
    const houseTitle = getOptionText(houseOption);
    const houseValueKey = String(houseOption.value);

    streetNode.children.push({
      key: `house:${houseValueKey}`,
      value: houseOption.value,
      title: houseTitle,
      search: [streetNode.search, houseTitle].filter(Boolean).join(' '),
      disabled: Boolean(houseOption.disabled),
    });
    houseValueMap.set(houseValueKey, houseOption.value);
  });

  const treeData = streetNodes
    .filter((streetNode) => streetNode.children.length)
    .map((streetNode) => ({
      ...streetNode,
      disableCheckbox: streetNode.children.every((houseNode) =>
        Boolean(houseNode.disabled)
      ),
    }));

  return { treeData, houseValueMap };
};

export const HouseTreeSelect = forwardRef(function HouseTreeSelect(
  {
    className,
    disabled,
    houseOptions = [],
    id,
    loading,
    name,
    onChange,
    streetOptions = [],
    value,
  },
  ref
) {
  const { treeData, houseValueMap } = useMemo(
    () => getHouseTreeData({ houseOptions, streetOptions }),
    [houseOptions, streetOptions]
  );
  const treeSelectValue = useMemo(() => {
    const selectedValues = Array.isArray(value) ? value : [];

    return selectedValues.map((houseValue) => {
      const houseValueKey = String(houseValue);

      return houseValueMap.has(houseValueKey)
        ? houseValueMap.get(houseValueKey)
        : houseValue;
    });
  }, [houseValueMap, value]);

  const filterTreeNode = (searchValue, treeNode) =>
    String(treeNode.props.search || '')
      .toLowerCase()
      .includes(String(searchValue || '').toLowerCase());

  const handleChange = (rawValues = []) => {
    const nextValues = [];
    const nextValueSet = new Set();
    const values = Array.isArray(rawValues) ? rawValues : [rawValues];

    const addHouseValue = (houseValue) => {
      const houseValueKey = String(houseValue);

      if (nextValueSet.has(houseValueKey)) {
        return;
      }

      nextValueSet.add(houseValueKey);
      nextValues.push(houseValue);
    };

    values.forEach((item) => {
      const itemKey = String(item);

      if (houseValueMap.has(itemKey)) {
        addHouseValue(houseValueMap.get(itemKey));
      }
    });

    if (typeof onChange === 'function') {
      onChange(nextValues);
    }
  };

  return (
    <TreeSelect
      ref={ref}
      className={className}
      value={treeSelectValue}
      disabled={disabled}
      id={id}
      name={name}
      treeData={treeData}
      treeCheckable
      multiple
      allowClear
      showSearch
      treeDefaultExpandAll
      treeNodeFilterProp="search"
      filterTreeNode={filterTreeNode}
      showCheckedStrategy={TreeSelect.SHOW_CHILD}
      placeholder="Дом"
      notFoundContent={loading ? 'Загрузка...' : 'Нет данных'}
      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
      onChange={handleChange}
    />
  );
});
